/**
 * 自定义指标组件
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { Popover, Button, Icon } from 'tea-component';
import Bubble from '@/pages/home/components/TeaComponents/Bubble';
import { getLanguage } from '@home/utils/common';
import { IEventFilterValue } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/components/EventFilter/components/EventFilterContent/type';
import EditInput from '../EditInput';
import { ICustomMetricItemProps } from './types';
import EventPanel from '../EventFilter/components/EventPanel';
import { IEventFilterItem } from '../EventFilter/components/EventPanel/type';
import { LANGUAGE_EN } from '@home/_config/config';
import './index.scss';

const DEFAULT_CUSTOM_METRIC_VALUE: IEventFilterItem = {
  id: uuidv4(),
  displayNameEn: '',
  displayNameCn: '',
  eventName: '',
  eventParameter: {
    type: 'market',
    regions: [],
    markets: [],
  },
  hoursSinceRegister: '',
  hoursSinceRegisterCustom: '',
  minValue: '',
  maxValue: '',
  aggregationType: '',
  occurCountOperator: '',
  occurCountValue: [],
};

const CustomMetricItem: React.FC<ICustomMetricItemProps> = ({
  optionCustomMetrics = [],
  eventList,
  column,
  index,
  handle,
  onUpdate,
}) => {
  const { removeItem, notifyItems } = handle;
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const [customMetricVisible, setCustomMetricVisible] = useState<boolean>(false);
  const [tempCustomMetricValue, setTempCustomMetricValue] = useState<IEventFilterItem>(
    (column as any).custom_metric_config || DEFAULT_CUSTOM_METRIC_VALUE
  );

  const hoursSinceRegisterOptions: { value: string; text: string; }[] = [
    { value: '24', text: '24' },
    { value: '72', text: '72' },
    { value: '168', text: '168' },
  ];

  useEffect(() => {
    if ((column as any).custom_metric_config) {
      setTempCustomMetricValue((column as any).custom_metric_config);
    }
  }, [(column as any).custom_metric_config]);

  const handleRename = (newValue: string) => {
    column.metric_alias = newValue;
    column.column_display = newValue;
    column.column_display_en = newValue;
    notifyItems();
    onUpdate(column);
  };

  const handleDelete = () => {
    removeItem(column);
  };

  const handleVisibleChange = useCallback((visible: boolean) => {
    setCustomMetricVisible(visible);
    if (!visible && (column as any).custom_metric_config) {
      setTempCustomMetricValue((column as any).custom_metric_config);
    }
  }, [column]);

  const handleCustomMetricConfirm = useCallback((filterValue?: IEventFilterValue) => {
    const finalValue = filterValue || tempCustomMetricValue;
    (column as any).custom_metric_config = finalValue;
    notifyItems();
    onUpdate(column);
    setCustomMetricVisible(false);
  }, [tempCustomMetricValue, column, notifyItems, onUpdate]);

  const handleCustomMetricCancel = useCallback(() => {
    if ((column as any).custom_metric_config) {
      setTempCustomMetricValue((column as any).custom_metric_config);
    } else {
      setTempCustomMetricValue(DEFAULT_CUSTOM_METRIC_VALUE);
    }
    setCustomMetricVisible(false);
  }, [column]);

  const eventOccurOptions = useMemo(() => {
    return optionCustomMetrics?.map((opt) => ({
      value: opt?.value,
      text: opt?.text || getLanguage(isEN, opt?.textEn, opt?.textCn),
      textEn: opt?.textEn,
      textCn: opt?.textCn,
    })) || [];
  }, [optionCustomMetrics, isEN, formatMessage]);

  const metricAlias = column.metric_alias || column.column_display || '';

  /** 显示名称由 EventPanel 回传 displayNameEn/displayNameCn，按语言取；兼容旧数据 displayName */
  const displayText =
    getLanguage(
      isEN,
      tempCustomMetricValue.displayNameEn,
      tempCustomMetricValue.displayNameCn
    ) ||
    (tempCustomMetricValue.displayName ??
      formatMessage({ id: 'dbl_ab_experiment_metric_filter_custom_placeholder' }));

  const isPlaceholder = !tempCustomMetricValue.eventName;

  return (
    <div className="model-port item-drag">
      <div className="context-title">
        <EditInput
          index={index}
          value={metricAlias}
          showIndex={true}
          showDragIcon={true}
          editTooltip={formatMessage({ id: 'dbl_ab_experiment_event_filter_edit_name' })}
          deleteTooltip={formatMessage({ id: 'dbl_ab_experiment_event_filter_delete' })}
          onRename={handleRename}
          onDelete={handleDelete}
          className="dbl-custom-metric-edit-input"
        />
      </div>
      <div className="context-two">
        <div className="total-tab">
          <Bubble content={isPlaceholder ? '' : displayText} placement="top" arrowPointAtCenter>
            <Popover
              visible={customMetricVisible}
              onVisibleChange={handleVisibleChange}
              trigger="click"
              placement="bottom-start"
              overlayClassName="dbl-custom-metric-overlay"
              overlay={
                <EventPanel
                  visible={customMetricVisible}
                  value={tempCustomMetricValue}
                  placeholderTitle={metricAlias}
                  onChange={(value) => setTempCustomMetricValue(value)}
                  onConfirm={(value) => handleCustomMetricConfirm(value)}
                  onCancel={handleCustomMetricCancel}
                  hoursSinceRegisterOptions={hoursSinceRegisterOptions}
                  data={eventList}
                  eventOccurOptions={eventOccurOptions}
                  displayConfig={{
                    eventOccurDisplay: 'aggregation',
                  }}
                />
              }
            >
              <Button
                type="weak"
                className={classNames('dbl-custom-metric-trigger', {
                  'dbl-custom-metric-trigger--placeholder': isPlaceholder,
                })}
              >
                <span className="dbl-custom-metric-trigger__text">
                  {displayText}
                </span>
                <Icon type="arrowdown" />
              </Button>
            </Popover>
          </Bubble>
        </div>
      </div>
    </div>
  );
};

export default CustomMetricItem;
